import React, { useEffect, useContext, useState, useRef, useCallback, useMemo } from "react";

// axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../BaseUrl";

// functions
import {
  _t,
  getCookie,
  formatPrice,
  getSystemSettings,
  customStyle,
} from "../../../../functions/Functions";
import { useTranslation } from "react-i18next";

// 3rd party packages
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Moment from "react-moment";
import moment from "moment";
import Select from "react-select";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useReactToPrint } from "react-to-print";

// pages & includes
import ReportSidebar from "./ReportSidebar";

// context consumer
import { SettingsContext } from "../../../../contexts/Settings";
import { RestaurantContext } from "../../../../contexts/Restaurant";
import CustomSpinLoader from "../../../components/CustomSpinLoader";
import { ToastError } from "../../../../functions/ToastShow";
import ReportTypeOptions from "../../../components/Reports/ReportTypeOptions";

const formatQty = (val) => {
  if (val === null || val === undefined || isNaN(val)) return "0";
  const num = parseFloat(val);
  return Number.isInteger(num) ? num.toString() : num.toFixed(3);
};

const formatMoney = (val) => {
  if (val === null || val === undefined || isNaN(val)) return "0.00";
  return (parseFloat(val) || 0).toFixed(3);
};

const formatPercent = (val) => {
  if (val === null || val === undefined || isNaN(val)) return "0.00%";
  return `${parseFloat(val).toFixed(2)}%`;
};

const ActualIdealCostGroupedTable = (props) => {
  const {
    header,
    reportData,
    tableHeading,
    isExport = false,
    exportType = "excel",
    printRef,
  } = props;

  const { rows, totals: grandTotals, hasData } = useMemo(() => {
    if (!reportData || typeof reportData !== "object" || Object.keys(reportData).length === 0) {
      return { rows: [], totals: {}, hasData: false };
    }

    const grandTotalsAcc = {
      sys_end_count: 0,
      phy_end_count: 0,
      ideal_cost: 0,
      actual_cost: 0,
      variance_stock: 0,
      variance_cost: 0,
    };

    const renderedRows = [];
    const branchKeys = Object.keys(reportData);

    branchKeys.forEach((branchName) => {
      const branchData = reportData[branchName] || {};
      const branchTotalsAcc = {
        sys_end_count: 0,
        phy_end_count: 0,
        ideal_cost: 0,
        actual_cost: 0,
        variance_stock: 0,
        variance_cost: 0,
      };

      // Branch Header
      renderedRows.push(
        <tr key={`branch-${branchName}`} style={{ backgroundColor: "#e9ecef" }}>
          <th
            align="left"
            className="border-1 border sm-text text-left"
            style={{ minWidth: "160px", fontWeight: "bold", backgroundColor: "#e9ecef", textAlign: "left" }}
          >
            {branchName}
          </th>
          <th align="left" className="border-1 border text-left" style={{ backgroundColor: "#e9ecef", textAlign: "left" }}></th>
          <th align="left" className="border-1 border text-left" style={{ backgroundColor: "#e9ecef", textAlign: "left" }}></th>
          {header.slice(3).map((_, i) => (
            <th key={i} align="right" className="border-1 border text-right" style={{ backgroundColor: "#e9ecef", textAlign: "right" }}></th>
          ))}
        </tr>
      );

      const groupKeys = Object.keys(branchData);

      groupKeys.forEach((groupName) => {
        const groupData = branchData[groupName] || {};
        const groupTotalsAcc = {
          sys_end_count: 0,
          phy_end_count: 0,
          ideal_cost: 0,
          actual_cost: 0,
          variance_stock: 0,
          variance_cost: 0,
        };

        // Group Header
        renderedRows.push(
          <tr key={`group-${branchName}-${groupName}`} style={{ backgroundColor: "#f8f9fa" }}>
            <th align="left" className="border-1 border text-left" style={{ backgroundColor: "#f8f9fa", textAlign: "left" }}></th>
            <th
              align="left"
              className="border-1 border sm-text text-left"
              style={{ minWidth: "150px", fontWeight: "bold", backgroundColor: "#f8f9fa", textAlign: "left" }}
            >
              {groupName}
            </th>
            <th align="left" className="border-1 border text-left" style={{ backgroundColor: "#f8f9fa", textAlign: "left" }}></th>
            {header.slice(3).map((_, i) => (
              <th key={i} align="right" className="border-1 border text-right" style={{ backgroundColor: "#f8f9fa", textAlign: "right" }}></th>
            ))}
          </tr>
        );

        const itemKeys = Object.keys(groupData);

        itemKeys.forEach((itemName) => {
          const itemVal = groupData[itemName];
          if (!itemVal) return;
          const itemsList = Array.isArray(itemVal) ? itemVal : [itemVal];

          itemsList.forEach((metrics, itemIdx) => {
            if (!metrics || typeof metrics !== "object") return;

            const displayName = metrics.item_name || metrics.name || itemName;
            const sysEnd = parseFloat(metrics.sys_end_count) || 0;
            const phyEnd = parseFloat(metrics.phy_end_count) || 0;
            const idealCost = parseFloat(metrics.ideal_cost) || 0;
            const actualCost = parseFloat(metrics.actual_cost) || 0;
            const varStock = parseFloat(metrics.variance_stock) || 0;
            const varCost = parseFloat(metrics.variance_cost) || 0;

            groupTotalsAcc.sys_end_count += sysEnd;
            groupTotalsAcc.phy_end_count += phyEnd;
            groupTotalsAcc.ideal_cost += idealCost;
            groupTotalsAcc.actual_cost += actualCost;
            groupTotalsAcc.variance_stock += varStock;
            groupTotalsAcc.variance_cost += varCost;

            renderedRows.push(
              <tr key={`item-${branchName}-${groupName}-${itemName}-${itemIdx}`}>
                <td align="left" className="border-1 border sm-text text-left" style={{ textAlign: "left" }}></td>
                <td align="left" className="border-1 border sm-text text-left" style={{ textAlign: "left" }}></td>
                <td
                  align="left"
                  className="border-1 border sm-text font-weight-bold text-left"
                  style={{ minWidth: "180px", textAlign: "left" }}
                >
                  {displayName}
                </td>
                <td align="right" className="border-1 border sm-text text-right" style={{ textAlign: "right" }}>{formatQty(metrics.sys_end_count)}</td>
                <td align="right" className="border-1 border sm-text text-right" style={{ textAlign: "right" }}>{formatQty(metrics.phy_end_count)}</td>
                <td align="right" className="border-1 border sm-text text-right" style={{ textAlign: "right" }}>{metrics.avg_rate}</td>
                <td align="right" className="border-1 border sm-text text-right" style={{ textAlign: "right" }}>{formatMoney(metrics.ideal_cost)}</td>
                <td align="right" className="border-1 border sm-text text-right" style={{ textAlign: "right" }}>{formatMoney(metrics.actual_cost)}</td>
                <td align="right" className="border-1 border sm-text text-right" style={{ textAlign: "right" }}>{formatQty(metrics.variance_stock)}</td>
                <td align="right" className="border-1 border sm-text text-right" style={{ textAlign: "right" }}>{formatMoney(metrics.variance_cost)}</td>
                <td align="right" className="border-1 border sm-text text-right" style={{ textAlign: "right" }}>{formatPercent(metrics.variance_percent)}</td>
              </tr>
            );
          });
        });

        // Group Total Row
        const groupVarPercent =
          groupTotalsAcc.ideal_cost !== 0
            ? ((groupTotalsAcc.variance_cost / groupTotalsAcc.ideal_cost) * 100).toFixed(2) + "%"
            : "0.00%";

        renderedRows.push(
          <tr
            key={`group-total-${branchName}-${groupName}`}
            style={{ backgroundColor: "#f1f3f5", fontWeight: "bold" }}
          >
            <th align="left" className="border-1 border sm-text text-left" style={{ textAlign: "left" }}></th>
            <th align="left" className="border-1 border sm-text text-left" style={{ fontWeight: "bold", textAlign: "left" }}>
              {`Group Total (${groupName})`}
            </th>
            <th align="left" className="border-1 border sm-text text-left" style={{ textAlign: "left" }}></th>
            <td align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
              <strong>{formatQty(groupTotalsAcc.sys_end_count)}</strong>
            </td>
            <td align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
              <strong>{formatQty(groupTotalsAcc.phy_end_count)}</strong>
            </td>
            <td align="right" className="border-1 border sm-text text-right" style={{ textAlign: "right" }}>-</td>
            <td align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
              <strong>{formatMoney(groupTotalsAcc.ideal_cost)}</strong>
            </td>
            <td align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
              <strong>{formatMoney(groupTotalsAcc.actual_cost)}</strong>
            </td>
            <td align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
              <strong>{formatQty(groupTotalsAcc.variance_stock)}</strong>
            </td>
            <td align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
              <strong>{formatMoney(groupTotalsAcc.variance_cost)}</strong>
            </td>
            <td align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
              <strong>{groupVarPercent}</strong>
            </td>
          </tr>
        );

        // Accumulate to branch
        branchTotalsAcc.sys_end_count += groupTotalsAcc.sys_end_count;
        branchTotalsAcc.phy_end_count += groupTotalsAcc.phy_end_count;
        branchTotalsAcc.ideal_cost += groupTotalsAcc.ideal_cost;
        branchTotalsAcc.actual_cost += groupTotalsAcc.actual_cost;
        branchTotalsAcc.variance_stock += groupTotalsAcc.variance_stock;
        branchTotalsAcc.variance_cost += groupTotalsAcc.variance_cost;
      });

      // Branch Total Row
      const branchVarPercent =
        branchTotalsAcc.ideal_cost !== 0
          ? ((branchTotalsAcc.variance_cost / branchTotalsAcc.ideal_cost) * 100).toFixed(2) + "%"
          : "0.00%";

      renderedRows.push(
        <tr key={`branch-total-${branchName}`} style={{ backgroundColor: "#e2e6ea", fontWeight: "bold" }}>
          <th align="left" className="border-1 border sm-text text-left" style={{ fontWeight: "bold", textAlign: "left" }}>
            {`Branch Total (${branchName})`}
          </th>
          <th align="left" className="border-1 border text-left" style={{ textAlign: "left" }}></th>
          <th align="left" className="border-1 border text-left" style={{ textAlign: "left" }}></th>
          <td align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
            <strong>{formatQty(branchTotalsAcc.sys_end_count)}</strong>
          </td>
          <td align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
            <strong>{formatQty(branchTotalsAcc.phy_end_count)}</strong>
          </td>
          <td align="right" className="border-1 border sm-text text-right" style={{ textAlign: "right" }}>-</td>
          <td align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
            <strong>{formatMoney(branchTotalsAcc.ideal_cost)}</strong>
          </td>
          <td align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
            <strong>{formatMoney(branchTotalsAcc.actual_cost)}</strong>
          </td>
          <td align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
            <strong>{formatQty(branchTotalsAcc.variance_stock)}</strong>
          </td>
          <td align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
            <strong>{formatMoney(branchTotalsAcc.variance_cost)}</strong>
          </td>
          <td align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
            <strong>{branchVarPercent}</strong>
          </td>
        </tr>
      );

      // Spacer between branches
      renderedRows.push(
        <tr key={`gap-${branchName}`} style={{ height: "20px" }}>
          <td colSpan={header.length} className="border-0"></td>
        </tr>
      );

      // Accumulate to grand total
      grandTotalsAcc.sys_end_count += branchTotalsAcc.sys_end_count;
      grandTotalsAcc.phy_end_count += branchTotalsAcc.phy_end_count;
      grandTotalsAcc.ideal_cost += branchTotalsAcc.ideal_cost;
      grandTotalsAcc.actual_cost += branchTotalsAcc.actual_cost;
      grandTotalsAcc.variance_stock += branchTotalsAcc.variance_stock;
      grandTotalsAcc.variance_cost += branchTotalsAcc.variance_cost;
    });

    return { rows: renderedRows, totals: grandTotalsAcc, hasData: branchKeys.length > 0 };
  }, [header, reportData]);

  if (!hasData) {
    if (isExport) {
      return null;
    }
    return (
      <div className="text-center p-4 bg-white mt-3 rounded border">
        <p className="text-muted mb-0">{_t("No data found")}</p>
      </div>
    );
  }

  const grandVarPercent =
    grandTotals.ideal_cost !== 0
      ? ((grandTotals.variance_cost / grandTotals.ideal_cost) * 100).toFixed(2) + "%"
      : "0.00%";

  const tableContent = (
    <table
      id={isExport ? "table-to-xls" : undefined}
      className="table table-bordered table-hover mb-0"
      style={{ fontSize: "0.85rem" }}
    >
      <thead className="thead-light">
        {isExport && exportType === "excel" && (
          <>
            <tr>
              <th colSpan={header.length} style={{ border: "0px", textAlign: "center" }}>
                {tableHeading?.()}
              </th>
            </tr>
            <tr>
              <th colSpan={header.length} style={{ border: "0px" }}></th>
            </tr>
          </>
        )}
        <tr>
          {header.map((col, i) => (
            <th
              key={i}
              scope="col"
              align={i >= 3 ? "right" : "left"}
              className={`border-1 border ${i >= 3 ? "text-right" : "text-left"}`}
              style={{
                fontWeight: "bold",
                fontSize: "0.78rem",
                whiteSpace: "nowrap",
                textAlign: i >= 3 ? "right" : "left",
              }}
            >
              {col.name}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="align-middle">
        {rows}
        <tr key="grand-total" style={{ backgroundColor: "#d6d8db", fontWeight: "bold" }}>
          <th align="left" className="border-1 border sm-text text-left" style={{ fontWeight: "bold", textAlign: "left" }}>
            Grand Total
          </th>
          <th align="left" className="border-1 border sm-text text-left" style={{ textAlign: "left" }}></th>
          <th align="left" className="border-1 border sm-text text-left" style={{ textAlign: "left" }}></th>
          <th align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
            {formatQty(grandTotals.sys_end_count)}
          </th>
          <th align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
            {formatQty(grandTotals.phy_end_count)}
          </th>
          <th align="right" className="border-1 border sm-text text-right" style={{ textAlign: "right" }}>-</th>
          <th align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
            {formatMoney(grandTotals.ideal_cost)}
          </th>
          <th align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
            {formatMoney(grandTotals.actual_cost)}
          </th>
          <th align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
            {formatQty(grandTotals.variance_stock)}
          </th>
          <th align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
            {formatMoney(grandTotals.variance_cost)}
          </th>
          <th align="right" className="border-1 border sm-text text-right" style={{ fontWeight: "bold", textAlign: "right" }}>
            {grandVarPercent}
          </th>
        </tr>
      </tbody>
    </table>
  );

  if (isExport) {
    return (
      <div className="d-none">
        <div ref={printRef}>
          <div className="fk-print container-fluid mt-3">
            {exportType !== "excel" && tableHeading?.()}
            {tableContent}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="table-responsive bg-white mt-3 rounded border" style={{ maxHeight: "65vh" }}>
      {tableContent}
    </div>
  );
};

const ActualIdealCostReport = () => {
  const { t } = useTranslation();

  // contexts
  const { loading, setLoading, dataPaginating, setDataPaginating, generalSettings } =
    useContext(SettingsContext);
  const { branchForSearch, getIngredientItem, ingredientItemForSearch } =
    useContext(RestaurantContext);

  // filters & report state
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [option, setOption] = useState("view");
  const [generatedReport, setGeneratedReport] = useState(false);
  const [reportData, setReportData] = useState(null);

  const componentRef = useRef();
  const componentRefThermal = useRef();

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 400);
  }, [generalSettings, setLoading]);

  useEffect(() => {
    if (!ingredientItemForSearch && typeof getIngredientItem === "function") {
      getIngredientItem();
    }
  }, [getIngredientItem, ingredientItemForSearch]);

  const thermalPrint = useReactToPrint({
    content: () => componentRefThermal.current,
    pageStyle: `
      @page {
        margin: 15px;
      }
    `,
  });

  const handleOption = (e) => setOption(e.target.value);

  const generateBranchName = useCallback(() => {
    if (selectedBranches && selectedBranches.length > 0) {
      return selectedBranches.map((b) => b.name).join(", ");
    }
    return _t(t("All Branches"));
  }, [selectedBranches, t]);

  const tableHeading = useCallback(
    () => (
      <>
        <h3 className="text-center xslg-text my-2">
          {getSystemSettings(generalSettings, "siteName")}
        </h3>
        <h3 className="text-center xslg-text my-2">
          {_t(t("Ideal vs Actual Cost Report"))}
        </h3>
        <p className="text-center my-1 font-weight-bold">{generateBranchName()}</p>
        <p className="text-center my-1">
          {startDate && endDate ? (
            <>
              FROM&nbsp;
              <Moment format="LL">{startDate}</Moment>
              &nbsp;TO&nbsp;
              <Moment format="LL">{endDate}</Moment>
            </>
          ) : (
            startDate && (
              <>
                Date&nbsp;
                <Moment format="LL">{startDate}</Moment>
              </>
            )
          )}
        </p>
      </>
    ),
    [generalSettings, generateBranchName, startDate, endDate, t]
  );

  const generateReport = () => {
    setReportData(null);
    setGeneratedReport(false);

    if (startDate === null || endDate === null) {
      ToastError({
        title: _t(t("Please select From date and To date to generate report")),
      });
      return;
    }

    const branchIds = (selectedBranches || []).map((b) => b.id);
    if (branchIds.length === 0) {
      ToastError({
        title: _t(t("Please select at least one branch to generate report")),
      });
      return;
    }

    setDataPaginating(true);
    const fromDate = moment(startDate).format("YYYY-MM-DD");
    const toDate = moment(endDate).format("YYYY-MM-DD");

    const url = BASE_URL + "/settings/ideal-vs-actual-cost-report";

    const payload = {
      from_date: fromDate,
      to_date: toDate,
      branch_id: branchIds,
    };

    if (selectedIngredients && selectedIngredients.length > 0) {
      payload.item_id = selectedIngredients.map((item) => item.id);
    }

    return axios
      .post(url, payload, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      })
      .then((res) => {
        setDataPaginating(false);
        setGeneratedReport(true);
        const data = res.data || {};
        setReportData(data);

        const hasData =
          data && typeof data === "object" && Object.keys(data).length > 0;

        if (!hasData) {
          toast.info(_t(t("No data found for the selected criteria")), {
            position: "bottom-center",
            autoClose: 3000,
          });
          return;
        }

        if (option === "excel") {
          setTimeout(() => componentRef.current?.handleDownload?.(), 150);
        } else if (option === "print") {
          setTimeout(() => thermalPrint(), 150);
        }
      })
      .catch((error) => {
        setDataPaginating(false);
        if (error.response?.data?.status === 0 || error.response?.data?.message) {
          ToastError({
            title: _t(t(error.response?.data?.message || "Please try again")),
          });
          return;
        }
        ToastError({ title: _t(t("Please try again")) });
      });
  };

  const header = useMemo(
    () => [
      { name: _t(t("Branch")), key: "branch" },
      { name: _t(t("Group")), key: "group" },
      { name: _t(t("Item Name")), key: "item_name" },
      { name: _t(t("Sys End Count")), key: "sys_end_count" },
      { name: _t(t("Phy End Count")), key: "phy_end_count" },
      { name: _t(t("Avg Rate")), key: "avg_rate" },
      { name: _t(t("Ideal Cost")), key: "ideal_cost" },
      { name: _t(t("Actual Cost")), key: "actual_cost" },
      { name: _t(t("Variance Stock")), key: "variance_stock" },
      { name: _t(t("Variance Cost")), key: "variance_cost" },
      { name: _t(t("Variance %")), key: "variance_percent" },
    ],
    [t]
  );

  return (
    <>
      <Helmet>
        <title>{_t(t("Ideal vs Actual Cost Report"))}</title>
      </Helmet>

      <ReactHTMLTableToExcel
        id="test-table-xls-button"
        className="download-table-xls-button btn btn-success float-right mr-3 invisible"
        table="table-to-xls"
        filename="Ideal vs Actual Cost Report"
        sheet="sheet 1"
        buttonText="Ideal vs Actual Cost Report"
        ref={componentRef}
      />

      {/* Hidden print/excel export container */}
      {reportData && Object.keys(reportData).length > 0 && (
        <ActualIdealCostGroupedTable
          header={header}
          reportData={reportData}
          tableHeading={tableHeading}
          isExport={true}
          exportType={option}
          printRef={componentRefThermal}
        />
      )}

      {/* Main Body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* Left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ReportSidebar />
            </div>

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div className="fk-scroll--pos-menu table-bottom-info-hide" data-simplebar>
                  <div className="t-pl-15 t-pr-15">
                    <CustomSpinLoader loading={loading || dataPaginating} />
                    <div>
                      {/* Top Bar with Title and Excel/Print Radios */}
                      <div className="row gx-2 align-items-center t-pt-15 t-pb-15">
                        <div className="col-6 t-mb-15 mb-md-0">
                          <ul className="t-list fk-breadcrumb">
                            <li className="fk-breadcrumb__list">
                              <span className="t-link fk-breadcrumb__link text-capitalize">
                                {_t(t("Ideal vs Actual Cost Report"))}
                              </span>
                            </li>
                          </ul>
                        </div>
                        <div className="col-6 d-flex justify-content-end">
                          <ReportTypeOptions
                            selectedOption={option}
                            onChange={handleOption}
                            view={true}
                          />
                        </div>
                      </div>

                      {/* Filters */}
                      <div className="row g-2 mt-2 align-items-end">
                        <div className="col-4 col-md-2 col-lg-2">
                          <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            dateFormat="dd/MM/yyyy"
                            dropdownMode="select"
                            className="form-control form-control-sm t-mb-15 mb-md-0"
                            placeholderText={_t(t("From date"))}
                            shouldCloseOnSelect={true}
                          />
                        </div>
                        <div className="col-4 col-md-2 col-lg-2">
                          <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            dateFormat="dd/MM/yyyy"
                            dropdownMode="select"
                            className="form-control form-control-sm t-mb-15 mb-md-0"
                            placeholderText={_t(t("To date"))}
                            shouldCloseOnSelect={true}
                          />
                        </div>
                        <div className="col-6 col-md-4 col-lg-4 t-mb-15 mb-md-0">
                          <Select
                            options={branchForSearch || []}
                            getOptionLabel={(opt) => opt.name}
                            getOptionValue={(opt) => opt.id}
                            isClearable
                            isMulti
                            value={selectedBranches}
                            onChange={(branches) => setSelectedBranches(branches || [])}
                            styles={customStyle}
                            maxMenuHeight="200px"
                            placeholder={_t(t("Select branch")) + ".."}
                          />
                        </div>
                        <div className="col-6 col-md-4 col-lg-4 t-mb-15 mb-md-0">
                          <Select
                            options={ingredientItemForSearch || []}
                            getOptionLabel={(opt) => opt.name}
                            getOptionValue={(opt) => opt.id}
                            isClearable
                            isMulti
                            value={selectedIngredients}
                            onChange={(items) => setSelectedIngredients(items || [])}
                            styles={customStyle}
                            maxMenuHeight="200px"
                            placeholder={_t(t("Select Ingredient Item")) + ".."}
                          />
                        </div>
                      </div>

                      {/* Action Button Row */}
                      <div className="row g-2 mt-3 justify-content-end align-items-center">
                        <div className="col-12 col-md-3 col-lg-2 text-right">
                          <button
                            className="btn btn-block btn-primary text-uppercase sm-text py-2 rounded"
                            onClick={generateReport}
                            disabled={dataPaginating}
                          >
                            {_t(t("Generate Report"))}
                          </button>
                        </div>
                      </div>

                      {/* On-screen Preview Table */}
                      {generatedReport && (
                        <div className="mt-4 pb-4">
                          <ActualIdealCostGroupedTable
                            header={header}
                            reportData={reportData}
                            tableHeading={tableHeading}
                            isExport={false}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Rightbar contents end */}
          </div>
        </div>
      </main>
    </>
  );
};

export default ActualIdealCostReport;
